import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getResultSignatories, postResultSignatory, putResultSignatory, deleteResultSignatory,
} from '@/api/owner';
import { Plus, X, Edit2, Trash2, Upload, AlertCircle, UserCheck, Check } from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};

interface SigForm {
  id?: number;
  name: string;
  role: string;
  signature_file: File | null;
  signature_preview: string | null;
  existing_path: string | null;
}

const emptyForm: SigForm = {
  name: '', role: '', signature_file: null, signature_preview: null, existing_path: null,
};

export function SignatoriesPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState<SigForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const sigQ = useQuery({
    queryKey: ['result-signatories'],
    queryFn: () => getResultSignatories().then((r: any) => r.data?.data ?? r.data),
  });
  const signatories = arr<any>(sigQ.data);

  const closeForm = () => { setForm(null); setError(null); };

  const openNew = () => { setForm({ ...emptyForm }); setError(null); };

  const openEdit = (s: any) => {
    const base = s.signature_path ? resolveStorageUrl(s.signature_path) : null;
    setForm({
      id: s.id,
      name: s.name ?? '',
      role: s.role ?? '',
      signature_file: null,
      signature_preview: base ? `${base}${base.includes('?') ? '&' : '?'}v=${Date.now()}` : null,
      existing_path: s.signature_path ?? null,
    });
    setError(null);
  };

  const pickFile = (f: File | null) => {
    if (!form) return;
    if (!f) {
      setForm({ ...form, signature_file: null, signature_preview: form.existing_path ? resolveStorageUrl(form.existing_path) : null });
      return;
    }
    setForm({ ...form, signature_file: f, signature_preview: URL.createObjectURL(f) });
  };

  const saveM = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('No form');
      let signature_path = form.existing_path;

      // If a new file is picked, upload it first
      if (form.signature_file) {
        const { uploadMedia } = await import('@/utils/upload');
        signature_path = await uploadMedia(form.signature_file);
      }

      const body = {
        name: form.name.trim(),
        role: form.role.trim(),
        signature_path,
      };

      return form.id ? putResultSignatory(form.id, body) : postResultSignatory(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['result-signatories'] });
      closeForm();
    },
    onError: (e: any) => {
      setError(e?.response?.data?.message ?? e?.message ?? 'Save failed');
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: number) => deleteResultSignatory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['result-signatories'] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Delete failed'),
  });

  const submitForm = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form) return;
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.role.trim()) { setError('Role is required (e.g. Principal, Class Teacher).'); return; }
    saveM.mutate();
  };

  return (
    <div className="max-w-[1200px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Results · Sheet Designer</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Signatories</h1>
          <p className="text-slate-500 text-sm mt-1">
            People whose signatures appear at the bottom of result sheets
          </p>
        </div>
        <button onClick={openNew}
          className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add signatory
        </button>
      </div>

      {sigQ.isLoading ? (
        <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
      ) : signatories.length === 0 ? (
        <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
          <UserCheck className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No signatories yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Add the principal, class teacher, or anyone whose signature belongs on the result sheet.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Signature</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {signatories.map((s: any) => (
                <tr key={s.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{s.name}</td>
                  <td className="px-4 py-3 text-slate-600">{s.role}</td>
                  <td className="px-4 py-3">
                    {s.signature_path ? (
                      <img
                        src={resolveStorageUrl(s.signature_path)}
                        alt="signature"
                        className="h-10 w-auto object-contain border border-slate-200 rounded bg-white p-1"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                          img.nextElementSibling && ((img.nextElementSibling as HTMLElement).style.display = 'inline');
                        }}
                      />
                    ) : (
                      <span className="text-xs italic text-slate-400">No signature</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(s)} className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={async () => { if (await askConfirm(`Delete signatory "${s.name}"?`)) deleteM.mutate(s.id); }}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form onSubmit={submitForm} className="bg-white w-full max-w-md rounded-2xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <h3 className="font-bold text-slate-900">{form.id ? 'Edit signatory' : 'New signatory'}</h3>
              <button type="button" onClick={closeForm} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required placeholder="e.g. Mrs A. Okafor"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Role *</label>
                <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  required placeholder="e.g. Principal, Head Teacher, Class Teacher"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Signature image</label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                  {form.signature_preview ? (
                    <div className="space-y-2">
                      <SigPreviewImage url={form.signature_preview} name={form.name} />
                      <div className="flex justify-center gap-2">
                        <button type="button" onClick={() => fileRef.current?.click()}
                          className="text-xs font-semibold text-slate-700 hover:text-slate-900 underline">
                          Change
                        </button>
                        <button type="button" onClick={() => pickFile(null)}
                          className="text-xs font-semibold text-red-600 hover:underline">
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileRef.current?.click()}
                      className="inline-flex flex-col items-center gap-1 text-slate-500 hover:text-slate-700">
                      <Upload className="w-6 h-6" />
                      <span className="text-sm font-semibold">Upload signature</span>
                      <span className="text-xs">PNG with transparent background works best</span>
                    </button>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Leave blank if this person's name should appear without a signature.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={closeForm}
                className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">
                Cancel
              </button>
              <button type="submit" disabled={saveM.isPending}
                className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
                {saveM.isPending ? 'Saving…' : 'Save signatory'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function resolveStorageUrl(path: string | null | undefined): string {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = (import.meta as any).env.VITE_API_URL ?? '';
  return `${base}/storage/${path.replace(/^\/+/, '')}`;
}


function SigPreviewImage({ url, name }: { url: string; name: string }) {
  const [broken, setBroken] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '').join('') || '?';

  if (broken) {
    return (
      <div className="h-20 mx-auto flex flex-col items-center justify-center gap-1">
        <div className="w-14 h-14 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-lg font-bold">
          {initials}
        </div>
        <p className="text-xs text-amber-700">Image failed to load</p>
      </div>
    );
  }

  return (
    <>
      {loading && <p className="text-xs text-slate-400 text-center">Loading…</p>}
      <img
        src={url}
        alt="signature preview"
        className="h-20 mx-auto object-contain bg-white"
        onLoad={() => setLoading(false)}
        onError={() => { setLoading(false); setBroken(true); }}
      />
    </>
  );
}
